import { EEditabilityType, ESdChangeReason } from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import { getSdInstance } from "../helpers";
import type { ToolHandlerGroup } from "../types";

export const visibilityTools: ToolHandlerGroup = {
  sync: {
    ui_component_get_visibility: (input) => {
      const { kuikaId } = input;
      if (!kuikaId) return { error: "kuikaId is required" };

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const di = dict[parseInt(kuikaId, 10)];
      if (!di || !di.item) return { error: `Component ${kuikaId} not found` };

      const visibility = di.item.visibility;
      if (!visibility) {
        return {
          kuikaId,
          visibilityType: "visible",
          hasCondition: false
        };
      }

      return {
        kuikaId,
        visibilityType: visibility.visibilityType || "visible",
        hasCondition: !!visibility.condition,
        condition: visibility.condition || undefined
      };
    },

    ui_component_set_visibility: (input) => {
      const { kuikaId, visibilityType } = input;
      if (!kuikaId) return { error: "kuikaId is required" };
      if (!visibilityType) return { error: "visibilityType is required" };

      const validTypes = ["visible", "hidden", "custom"];
      if (!validTypes.includes(visibilityType)) {
        return { error: `visibilityType must be one of: ${validTypes.join(", ")}` };
      }

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const di = dict[parseInt(kuikaId, 10)];
      if (!di || !di.item) return { error: `Component ${kuikaId} not found` };

      const visibility = di.item.visibility ? { ...di.item.visibility } : { id: Guid.create().toString() };
      visibility.visibilityType = visibilityType;

      if (visibilityType !== "custom") {
        visibility.condition = undefined;
      }

      di.item.visibility = visibility;
      const screenDesign = sd.screenProvider.getScreenDesign();
      sd.screenProvider.setScreenDesign(screenDesign, ESdChangeReason.UpdateVisibilty);

      return {
        success: true,
        kuikaId,
        visibilityType,
        message: `Visibility set to "${visibilityType}" on component ${kuikaId}`
      };
    },

    ui_component_get_editability: (input) => {
      const { kuikaId } = input;
      if (!kuikaId) return { error: "kuikaId is required" };

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const di = dict[parseInt(kuikaId, 10)];
      if (!di) return { error: `Component ${kuikaId} not found` };

      const editability = di.editability;
      if (!editability) {
        return {
          kuikaId,
          editabilityType: "enabled",
          hasCondition: false
        };
      }

      const typeLabels: Record<number, string> = {
        [EEditabilityType.Enabled]: "enabled",
        [EEditabilityType.Disabled]: "disabled",
        [EEditabilityType.Custom]: "custom"
      };

      return {
        kuikaId,
        editabilityType: typeLabels[editability.editabilityType] || "enabled",
        hasCondition: !!editability.condition,
        condition: editability.condition || undefined
      };
    },

    ui_component_set_editability: (input) => {
      const { kuikaId, editabilityType } = input;
      if (!kuikaId) return { error: "kuikaId is required" };
      if (!editabilityType) return { error: "editabilityType is required" };

      const typeMap: Record<string, number> = {
        enabled: EEditabilityType.Enabled,
        disabled: EEditabilityType.Disabled,
        custom: EEditabilityType.Custom
      };

      if (!(editabilityType in typeMap)) {
        return { error: `editabilityType must be one of: ${Object.keys(typeMap).join(", ")}` };
      }

      const sd = getSdInstance();
      const dict = sd.screenProvider.state.screenDesignDictionary;
      const di = dict[parseInt(kuikaId, 10)];
      if (!di) return { error: `Component ${kuikaId} not found` };

      const editability = di.editability ? { ...di.editability } : { editabilityType: EEditabilityType.Enabled };
      editability.editabilityType = typeMap[editabilityType];

      if (editabilityType !== "custom") {
        editability.condition = undefined;
      }

      di.editability = editability;
      const screenDesign = sd.screenProvider.getScreenDesign();
      sd.screenProvider.setScreenDesign(screenDesign, ESdChangeReason.UpdateEditability);

      return {
        success: true,
        kuikaId,
        editabilityType,
        message: `Editability set to "${editabilityType}" on component ${kuikaId}`
      };
    }
  }
};
